import {
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Edit,
  FileText,
  Film,
  GripVertical,
  Loader2,
  Monitor,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import 'katex/dist/katex.min.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { courseService } from '@/services/course.service';
import EditContentItemModal from './EditContentItemModal';
import UploadContentFileModal from './UploadContentFileModal';
import UploadContentVideoModal from './UploadContentVideoModal';
import EditVideoContentItemModal from './EditVideoContentItemModal';
import { isHtmlEffectivelyEmpty } from '@/utils/htmlContent';
import { getContentActualDuration, secondsToDuration } from '@/utils/timeFormat';
import EditSceneModal from './EditSceneModal';
import EditAvatarVideoModal from './EditAvatarVideoModal';
import SortableContentItem from './SortableContentItem';
import SortableScene from './SortableScene';
import AddContentItemModal from './AddContentItemModal';
import AddSceneModal from './AddSceneModal';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import {
  ContentItem,
  Duration,
  Scene,
  SlideThemeEnum,
  ContentFormatEnum
} from '@/types/course.types';
import HTMLContentPlayer from '@/components/ai-learn/HTMLContentPlayer';
import ScormPreview from './ScormPreview';
const CustomVideoPlayer = lazy(() => import('./CustomVideoPlayer'));

interface ContentItemsProps {
  items: ContentItem[];
  expanded: boolean;
  onToggle: () => void;
  getContentTypeIcon: (type: string) => React.ReactElement;
  formatDuration: (duration?: Duration | null) => string;
  theme?: SlideThemeEnum;
  videoRenderingMode?: 'animated' | 'static';
  contentFormat?: ContentFormatEnum;
  onViewAssessment?: (assessmentId: string) => void;
  moduleId?: string;
  onRefresh?: (moduleId: string) => Promise<void>;
  courseLanguage?: string;
  isGenerating?: boolean;
  isScormCourse?: boolean;
  /** True when the course structure is locked (SCORM / avatar-video): no
   *  add/edit/delete of content items or scenes — reorder still allowed. */
  structureLocked?: boolean;
  /** True when the course is published. Publishing is final, so nothing here is
   *  editable — not even reorder or AI regeneration. Stricter than structureLocked. */
  readOnly?: boolean;
  /** Open the add-assessment modal for this content item (PRE/POST). */
  onAddAssessment?: (type: 'PRE' | 'POST', contentItemId: string) => void;
  /** Request soft-delete of a content-item assessment (passes id + label). */
  onDeleteAssessment?: (assessmentId: string, label: string) => void;
}

export default function ContentItems({
  items,
  expanded,
  onToggle,
  getContentTypeIcon,
  formatDuration,
  theme = SlideThemeEnum.EDUCATIONAL,
  videoRenderingMode = 'animated',
  contentFormat,
  onViewAssessment,
  moduleId,
  onRefresh,
  courseLanguage = 'en-US',
  isGenerating = false,
  isScormCourse = false,
  structureLocked = false,
  readOnly = false,
  onAddAssessment,
  onDeleteAssessment
}: ContentItemsProps) {

  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [loaded_item_durations, setLoadedItemDurations] = useState<Record<string, number>>({});

  // Local copy of items so drag-reorder can update optimistically without a refetch.
  const [orderedItems, setOrderedItems] = useState<ContentItem[]>(items);
  useEffect(() => { setOrderedItems(items); }, [items]);

  // Delete confirmation state
  const [deletingItem, setDeletingItem] = useState<ContentItem | null>(null);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [isReorderingItems, setIsReorderingItems] = useState(false);
  // Add content item state
  const [showAddItem, setShowAddItem] = useState(false);
  // Scene add / delete state
  const [addSceneForItemId, setAddSceneForItemId] = useState<string | null>(null);
  const [deletingScene, setDeletingScene] = useState<{ scene: Scene; contentItemId: string } | null>(null);
  const [isDeletingScene, setIsDeletingScene] = useState(false);

  // Content items can be edited/reordered/deleted unless the course is
  // generating or SCORM-imported.
  // Add / edit / delete of content items + scenes: blocked while generating,
  // and on locked-structure courses (SCORM / avatar-video).
  // A published course is read-only, so treat it like `isGenerating` for every
  // interactive control below — that flag already gates them all.
  const locked = isGenerating || readOnly;
  // Tooltip copy differs: generating is temporary, published is permanent.
  const lockedReason = readOnly
    ? 'Published courses are read-only'
    : 'Cannot edit while course is generating';

  const canEditItems = !locked && !isScormCourse && !structureLocked;
  // Editing an avatar video is not a structural change, so the avatar structure
  // lock does not apply — only SCORM, generating and published do. Without this the
  // button is unreachable on any course that HAS content_delivery_type:'avatar_video'
  // stored, while still appearing on older avatar courses where that field is absent
  // (structureLocked is derived from it), which is why it showed on production but
  // not locally.
  const canEditAvatarVideo = !locked && !isScormCourse;
  // Reorder is allowed even on locked-structure courses (rearranging only, not
  // editing) — but never on a published course.
  const canReorderItems = !locked;
  // Manual assessment creation: allowed on avatar/heygen too — only SCORM +
  // generating/published are excluded.
  const canManageAssessments = !locked && !isScormCourse;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  // Separate sensor set for the nested scene drag-and-drop.
  const sceneSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Audio player state
  const [audioPlayerStates, setAudioPlayerStates] = useState<{
    [itemId: string]: {
      isPlaying: boolean;
      playbackRate: number;
      volume: number;
    }
  }>({});

  // Content editing state
  const [editingContentItem, setEditingContentItem] = useState<ContentItem | null>(null);
  const [uploadDocItem, setUploadDocItem] = useState<ContentItem | null>(null);
  const [uploadVideoItem, setUploadVideoItem] = useState<ContentItem | null>(null);
  const [editingVideoItem, setEditingVideoItem] = useState<ContentItem | null>(null);
  const [removingVideoId, setRemovingVideoId] = useState<string | null>(null);
  const [editingAvatarVideo, setEditingAvatarVideo] = useState<ContentItem | null>(null);
  const [editingSceneRef, setEditingSceneRef] = useState<{ sceneId: string; contentItemId: string } | null>(null);
  // Derive fresh scene from latest items to avoid stale data after save
  const editingScene = editingSceneRef ? (() => {
    const item = items.find(i => i._id === editingSceneRef.contentItemId);
    const scene = item?.scenes?.find(s => s._id === editingSceneRef.sceneId);
    return scene ? { scene, contentItemId: editingSceneRef.contentItemId } : null;
  })() : null;
  const [regeneratingVideoId, setRegeneratingVideoId] = useState<string | null>(null);

  // Poll regenerating scenes — calls immediately, then every 5 seconds
  // Refreshes parent data when any scene finishes regenerating
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const regeneratingSceneIds = items.flatMap(item =>
      (item.scenes || []).filter(s => s.is_regenerating).map(s => s._id!)
    ).filter(Boolean);

    if (regeneratingSceneIds.length === 0) return;

    const checkScenes = async () => {
      try {
        let anyFinished = false;
        for (const sceneId of regeneratingSceneIds) {
          const status = await courseService.getSceneStatus(sceneId);
          if (!status.is_regenerating) {
            anyFinished = true;
          }
        }
        if (anyFinished && onRefresh && moduleId) {
          await onRefresh(moduleId);
        }
      } catch (_err) {
        // Ignore polling errors
      }
    };

    // Call immediately on mount
    checkScenes();

    // Then every 5 seconds
    pollingRef.current = setInterval(checkScenes, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [items, onRefresh, moduleId]);

  // Regenerate video handler — queues via Kafka, then polls for completion
  const videoPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleRegenerateVideo = async (content_item_id: string) => {
    setRegeneratingVideoId(content_item_id);
    try {
      toast.loading('Queuing video merge...', { id: 'regen-video' });
      await courseService.regenerateContentItemVideo(content_item_id);
      toast.loading('Merging scene videos... This may take 30-60 seconds', { id: 'regen-video' });

      // Poll for completion
      if (videoPollingRef.current) clearInterval(videoPollingRef.current);
      videoPollingRef.current = setInterval(async () => {
        try {
          const status = await courseService.getContentItemVideoStatus(content_item_id);
          const gen_status = status.generation_status?.status;

          if (gen_status === 'completed') {
            clearInterval(videoPollingRef.current!);
            videoPollingRef.current = null;
            setRegeneratingVideoId(null);
            toast.success('Video merged successfully!', { id: 'regen-video', duration: 5000 });
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          } else if (gen_status === 'failed') {
            clearInterval(videoPollingRef.current!);
            videoPollingRef.current = null;
            setRegeneratingVideoId(null);
            toast.error(status.generation_status?.error || 'Video regeneration failed', { id: 'regen-video' });
          }
        } catch (_pollErr) {
          // Ignore polling errors
        }
      }, 5000);
    } catch (error: any) {
      toast.error(error.message || 'Failed to queue video regeneration', { id: 'regen-video' });
      setRegeneratingVideoId(null);
    }
  };

  // Cleanup video polling on unmount
  useEffect(() => {
    return () => {
      if (videoPollingRef.current) {
        clearInterval(videoPollingRef.current);
        videoPollingRef.current = null;
      }
    };
  }, []);

  // Audio player helper functions
  const getAudioPlayerState = (itemId: string) => {
    return audioPlayerStates[itemId] || {
      isPlaying: false,
      playbackRate: 1,
      volume: 1
    };
  };

  const updateAudioPlayerState = (itemId: string, updates: Partial<{ isPlaying: boolean; playbackRate: number; volume: number }>) => {
    setAudioPlayerStates(prev => ({
      ...prev,
      [itemId]: {
        ...getAudioPlayerState(itemId),
        ...updates
      }
    }));
  };

  const handleAudioPlayPause = (itemId: string) => {
    const currentState = getAudioPlayerState(itemId);
    updateAudioPlayerState(itemId, { isPlaying: !currentState.isPlaying });
  };

  const handleAudioPlaybackRateChange = (itemId: string) => {
    const currentState = getAudioPlayerState(itemId);
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(currentState.playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    updateAudioPlayerState(itemId, { playbackRate: nextRate });
  };

  const handleAudioVolumeChange = (itemId: string, volume: number) => {
    updateAudioPlayerState(itemId, { volume });
  };

  // Helper function to get theme class
  const getThemeClass = (): string => {
    const themeClasses = {
      [SlideThemeEnum.EDUCATIONAL]: 'theme-educational',
      [SlideThemeEnum.MODERN]: 'theme-modern',
      [SlideThemeEnum.CORPORATE]: 'theme-corporate',
      [SlideThemeEnum.DARK]: 'theme-dark',
      [SlideThemeEnum.MINIMAL]: 'theme-minimal'
    };
    return themeClasses[theme] || themeClasses[SlideThemeEnum.EDUCATIONAL];
  };

  // Helper function to get content format classes
  const getContentFormatClass = (): string => {
    const formatClasses = {
      [ContentFormatEnum.MARKDOWN]: 'content-format-markdown',
      [ContentFormatEnum.PLAIN]: 'content-format-plain',
      [ContentFormatEnum.AUDIO_FRIENDLY]: 'content-format-audio-friendly',
      [ContentFormatEnum.HTML_SLIDES]: 'content-format-html-slides',
      [ContentFormatEnum.HTML]: 'content-format-html'
    };
    return contentFormat ? formatClasses[contentFormat] || '' : '';
  };

  const handleItemToggle = (itemId: string | undefined) => {
    if (!itemId) return;
    const newExpanded = expandedItem === itemId ? null : itemId;
    setExpandedItem(newExpanded);
  };

  // Stable sortable id per content item (matches the key used in render).
  const getItemSortableId = (item: ContentItem, index: number): string =>
    item._id || index.toString();

  const handleContentDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !moduleId) return;

    // Only the displayed (non-assessment) items participate in reordering.
    const displayed = orderedItems.filter(i => i.type !== 'assessment');
    const others = orderedItems.filter(i => i.type === 'assessment');
    const ids = displayed.map((it, i) => getItemSortableId(it, i));
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const newDisplayed = arrayMove(displayed, oldIndex, newIndex);
    const previous = orderedItems;

    // Optimistic update, then persist. Revert on failure.
    setOrderedItems([...newDisplayed, ...others]);
    setIsReorderingItems(true);
    try {
      const orderedIds = newDisplayed.map(i => i._id).filter(Boolean) as string[];
      await courseService.reorderContentItems(moduleId, orderedIds);
      toast.success('Content order updated');
    } catch (error: any) {
      setOrderedItems(previous);
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to reorder content items'
      );
    } finally {
      setIsReorderingItems(false);
    }
  };

  const handleAddContentItem = async (data: { title: string }) => {
    if (!moduleId) return;
    try {
      // Type + design theme are derived from the course server-side.
      await courseService.createContentItem(moduleId, data);
      toast.success('Content item added');
      setShowAddItem(false);
      if (onRefresh) {
        await onRefresh(moduleId);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.message ||
          'Failed to add content item'
      );
      // Rethrow so the modal stays open.
      throw error;
    }
  };

  // Remove a directly-uploaded video, restoring the scene-based flow.
  const handleRemoveVideo = async (contentItemId: string) => {
    setRemovingVideoId(contentItemId);
    const loading = toast.loading('Removing uploaded video...');
    try {
      await courseService.removeContentItemVideo(contentItemId);
      toast.success('Uploaded video removed', { id: loading });
      if (onRefresh && moduleId) {
        await onRefresh(moduleId);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove uploaded video', { id: loading });
    } finally {
      setRemovingVideoId(null);
    }
  };

  const handleAddScene = async (contentItemId: string, title: string) => {
    if (!contentItemId) return;
    try {
      await courseService.createScene(contentItemId, { title });
      toast.success('Scene added');
      setAddSceneForItemId(null);
      if (onRefresh && moduleId) {
        await onRefresh(moduleId);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to add scene'
      );
      // Rethrow so the modal stays open and preserves the entered title.
      throw error;
    }
  };

  const handleSceneDragEnd = async (
    contentItemId: string,
    sceneList: Scene[],
    event: DragEndEvent
  ) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ids = sceneList.map((s, i) => s._id || i.toString());
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const newScenes = arrayMove(sceneList, oldIndex, newIndex);
    const previous = orderedItems;

    // Optimistic: update the matching item's scenes, then persist.
    setOrderedItems(prev => prev.map(it => it._id === contentItemId ? { ...it, scenes: newScenes } : it));
    try {
      const orderedIds = newScenes.map(s => s._id).filter(Boolean) as string[];
      await courseService.reorderScenes(contentItemId, orderedIds);
      toast.success('Scene order updated');
    } catch (error: any) {
      setOrderedItems(previous);
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to reorder scenes'
      );
    }
  };

  const handleDeleteScene = async () => {
    if (!deletingScene?.scene?._id) return;
    setIsDeletingScene(true);
    try {
      await courseService.deleteScene(deletingScene.scene._id);
      toast.success('Scene deleted');
      setDeletingScene(null);
      if (onRefresh && moduleId) {
        await onRefresh(moduleId);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to delete scene'
      );
    } finally {
      setIsDeletingScene(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deletingItem?._id) return;
    setIsDeletingItem(true);
    try {
      await courseService.deleteContentItem(deletingItem._id);
      toast.success('Content item deleted');
      setDeletingItem(null);
      if (onRefresh && moduleId) {
        await onRefresh(moduleId);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to delete content item'
      );
    } finally {
      setIsDeletingItem(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border">
      <div
        className="flex items-center justify-between p-4 border-b cursor-pointer hover:bg-gray-50 rounded-t-xl"
        onClick={onToggle}
      >
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Content
          {isReorderingItems && <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-blue-500" />}
        </h3>
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
          expanded ? 'rotate-90' : ''
        }`} />
      </div>
      {expanded && (
        <div className="p-4 space-y-4">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleContentDragEnd}>
            <SortableContext
              items={orderedItems.filter(i => i.type !== 'assessment').map((it, i) => getItemSortableId(it, i))}
              strategy={verticalListSortingStrategy}
            >
          {orderedItems.filter(item => item.type !== 'assessment').map((item, index) => {
            const isExpanded = expandedItem === item._id;
            const itemKey = getItemSortableId(item, index);
            // Drag only while the item is collapsed (and editing is allowed).
            const dragDisabled = !canReorderItems || isExpanded;

            return (
              <SortableContentItem key={itemKey} id={itemKey} disabled={dragDisabled}>
                {({ attributes, listeners }) => (
                  <>
                <div
                  className={`p-3 cursor-pointer transition-all ${
                    isExpanded ? 'border-b' : 'hover:opacity-90'
                  }`}
                  onClick={() => handleItemToggle(item._id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Drag handle (collapsed items only; also on SCORM) */}
                      {!dragDisabled && (
                        <button
                          type="button"
                          {...attributes}
                          {...listeners}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-shrink-0 p-0.5 -ml-0.5 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
                          title="Drag to reorder"
                          aria-label="Drag to reorder content item"
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                      )}
                      <span className="text-gray-600">
                        {getContentTypeIcon(item.type)}
                      </span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">{item.title}</h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                          {/* SCORM: no estimated time (completion is CMI/manual) — hide for
                              SCORM content items (type 'scorm') regardless of the course flag. */}
                          {/* The player reports SECONDS as a number; formatDuration reads
                              .hours/.minutes, so a raw number rendered as "0m". */}
                          {!isScormCourse && item.type !== 'scorm' && (() => {
                            const loaded_seconds = loaded_item_durations[item._id ?? ''];
                            const shown_duration =
                              (loaded_seconds > 0 ? secondsToDuration(loaded_seconds) : null) ||
                              getContentActualDuration(item) ||
                              item.duration ||
                              item.estimated_time;
                            return !shown_duration ? null : (
                            <span>
                              {formatDuration(shown_duration)} to {
                                item.type === 'video_presentation' || item.type === 'avatar_presentation' || item.type === 'avatar_video' ? 'watch' :
                                item.type === 'html_with_audio' ? 'listen' :
                                'complete'
                              }
                            </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {/* Upload document — available on any html / html_with_audio
                            item that isn't already backed by an uploaded file, whether
                            or not it has authored HTML. Once a file IS uploaded the item
                            renders the file instead and this is replaced by the
                            remove-upload action in the edit modal. */}
                        {canEditItems && !isGenerating &&
                          (item.type === 'html' || item.type === 'html_with_audio') &&
                          item.content_source !== 'uploaded' && (
                          <button
                            onClick={() => setUploadDocItem(item)}
                            className="p-1.5 rounded-lg transition-colors hover:bg-brand-indigo-100"
                            title={
                              isHtmlEffectivelyEmpty(item.content)
                                ? 'Upload a PDF file for this content'
                                : 'Upload a PDF file — this replaces the current content'
                            }
                          >
                            <Upload className="w-3.5 h-3.5 text-brand-indigo-600" />
                          </button>
                        )}
                        {/* Upload video — only for a video_presentation item with
                            NO scenes that isn't already an uploaded video. */}
                        {canEditItems && !isGenerating &&
                          item.type === 'video_presentation' &&
                          item.content_source !== 'uploaded' &&
                          (!item.scenes || item.scenes.length === 0) && (
                          <button
                            onClick={() => setUploadVideoItem(item)}
                            className="p-1.5 rounded-lg transition-colors hover:bg-brand-indigo-100"
                            title="Upload a video (MP4/AVI/MOV) for this content"
                          >
                            <Upload className="w-3.5 h-3.5 text-brand-indigo-600" />
                          </button>
                        )}
                        {/* Edit uploaded video — minimal edit (title + estimated
                            time only); the video itself is fixed. */}
                        {canEditItems && !isGenerating &&
                          item.type === 'video_presentation' &&
                          item.content_source === 'uploaded' && (
                          <button
                            onClick={() => setEditingVideoItem(item)}
                            className="p-1.5 rounded-lg transition-colors hover:bg-brand-blue-100"
                            title="Edit title & estimated time"
                          >
                            <Edit className="w-3.5 h-3.5 text-brand-blue-600" />
                          </button>
                        )}
                        {/* Edit Content Button (hidden for video_presentation).
                            Uploaded files open a read-only preview where only the
                            title / time can be edited (not the HTML). */}
                        {item.type !== 'video_presentation' && item.type !== 'avatar_presentation' && item.type !== 'avatar_video' && item.type !== 'scorm' && (
                          <button
                            onClick={() => !locked && setEditingContentItem(item)}
                            disabled={locked}
                            className={`p-1.5 rounded-lg transition-colors ${
                              locked
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:bg-brand-blue-100'
                            }`}
                            title={locked ? lockedReason : 'Edit content'}
                          >
                            <Edit className={`w-3.5 h-3.5 ${locked ? 'text-gray-400' : 'text-brand-blue-600'}`} />
                          </button>
                        )}
                        {/* Edit Avatar Video Button — allowed on avatar courses; only
                            SCORM / generating / published block it. */}
                        {canEditAvatarVideo && item.type === 'avatar_video' && (
                          <button
                            onClick={() => !locked && setEditingAvatarVideo(item)}
                            disabled={locked}
                            className={`p-1.5 rounded-lg transition-colors ${
                              locked
                                ? 'cursor-not-allowed opacity-50'
                                : 'hover:bg-brand-purple-100'
                            }`}
                            title={locked ? lockedReason : 'Edit avatar video'}
                          >
                            <Edit className={`w-3.5 h-3.5 ${locked ? 'text-gray-400' : 'text-brand-purple-600'}`} />
                          </button>
                        )}
                        {/* Delete Content Item Button */}
                        {canEditItems && (
                          <button
                            onClick={() => setDeletingItem(item)}
                            className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
                            title="Delete content item"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t">
                    {(item.type === 'video_presentation' || item.type === 'avatar_presentation' || item.type === 'avatar_video') && (
                      <div>
                        {item.media_assets?.s3_video_url ? (
                          <Suspense fallback={<div className="flex items-center justify-center py-8"><div className="w-8 h-8 border-4 border-brand-purple-200 border-t-brand-purple-500 rounded-full animate-spin" /></div>}>
                            <CustomVideoPlayer
                              src={item.media_assets.s3_video_url}
                              captionedSrc={item.media_assets?.s3_captioned_video_url}
                              title={item.title}
                              onDurationChange={(dur) => setLoadedItemDurations(prev => ({ ...prev, [item._id]: dur }))}
                            />
                          </Suspense>
                        ) : (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Monitor className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">Video not available</p>
                            <p className="text-sm text-gray-400 mt-1">The video is currently being prepared</p>
                          </div>
                        )}

                        {/* Uploaded video — offer "Remove video" so the item can
                            go back to the scene-based flow. Only for a directly
                            uploaded video_presentation video. */}
                        {canEditItems && item.type === 'video_presentation' &&
                          item.content_source === 'uploaded' && item.media_assets?.s3_video_url && (
                          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                            <span className="text-xs text-gray-500 flex items-center gap-1.5">
                              <Film className="w-3.5 h-3.5 text-brand-indigo-500" />
                              Uploaded video — no scenes are used.
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (removingVideoId !== item._id) handleRemoveVideo(item._id!);
                              }}
                              disabled={removingVideoId === item._id}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border border-red-200 text-red-600 bg-white hover:bg-red-50 transition-colors disabled:opacity-50"
                              title="Remove the uploaded video (keeps the content item)"
                            >
                              {removingVideoId === item._id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Trash2 className="w-3.5 h-3.5" />}
                              {removingVideoId === item._id ? 'Removing…' : 'Remove video'}
                            </button>
                          </div>
                        )}

                        {/* Scenes List — shown when the item has scenes, or when
                            scenes are editable (video courses) so an empty scene
                            list still offers "Add Scene". Hidden for an uploaded
                            video (no scene flow) and locked courses (avatar/SCORM)
                            with no scenes. */}
                        {item.content_source !== 'uploaded' &&
                          ((item.scenes && item.scenes.length > 0) || canEditItems) && (
                          <div className="border-t border-gray-200">
                            <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Film className="w-4 h-4 text-brand-purple-600" />
                                <span className="text-sm font-semibold text-gray-900">Scenes</span>
                                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{item.scenes?.length || 0}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Add Scene */}
                                {canEditItems && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddSceneForItemId(item._id!);
                                    }}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors bg-white text-brand-purple-700 border border-brand-purple-200 hover:bg-brand-purple-50"
                                    title="Add a new scene"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add Scene
                                  </button>
                                )}
                                {/* Merge Scenes (only meaningful when scenes exist) */}
                                {item.scenes && item.scenes.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!locked && !regeneratingVideoId) {
                                        handleRegenerateVideo(item._id!);
                                      }
                                    }}
                                    disabled={locked || regeneratingVideoId === item._id}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                                      locked || regeneratingVideoId === item._id
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-brand-purple-600 text-white hover:bg-brand-purple-700'
                                    }`}
                                    title={locked ? lockedReason : 'Merge all scene videos into final video'}
                                  >
                                    <RefreshCw className={`w-3.5 h-3.5 ${regeneratingVideoId === item._id ? 'animate-spin' : ''}`} />
                                    {regeneratingVideoId === item._id ? 'Merging...' : 'Merge Scenes'}
                                  </button>
                                )}
                              </div>
                            </div>
                            {(!item.scenes || item.scenes.length === 0) && (
                              <div className="px-4 py-6 text-center text-sm text-gray-400">
                                No scenes yet. Click "Add Scene" to create one.
                              </div>
                            )}
                            <DndContext
                              sensors={sceneSensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(e) => handleSceneDragEnd(item._id!, item.scenes || [], e)}
                            >
                            <SortableContext
                              items={(item.scenes || []).map((s, i) => s._id || i.toString())}
                              strategy={verticalListSortingStrategy}
                            >
                            <div className="divide-y divide-gray-100">
                              {(item.scenes || []).map((scene, scene_index) => {
                                const sceneKey = scene._id || scene_index.toString();
                                const sceneDragDisabled = !canEditItems || scene.is_regenerating;
                                return (
                                <SortableScene
                                  key={sceneKey}
                                  id={sceneKey}
                                  disabled={sceneDragDisabled}
                                  className={`px-4 py-3 flex items-center gap-3 transition-colors ${scene.is_regenerating ? 'bg-purple-50/50' : 'hover:bg-gray-50'}`}
                                >
                                  {({ attributes, listeners }) => (
                                  <>
                                  {scene.is_regenerating && (
                                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded">
                                      <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-full">
                                        <Loader2 className="w-3.5 h-3.5 text-purple-600 animate-spin" />
                                        <span className="text-xs font-medium text-purple-700">Generating...</span>
                                      </div>
                                    </div>
                                  )}
                                  {/* Scene drag handle (collapsed / not-regenerating only) */}
                                  {!sceneDragDisabled && (
                                    <button
                                      type="button"
                                      {...attributes}
                                      {...listeners}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex-shrink-0 p-0.5 -ml-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
                                      title="Drag to reorder scene"
                                      aria-label="Drag to reorder scene"
                                    >
                                      <GripVertical className="w-4 h-4" />
                                    </button>
                                  )}
                                  <div className="w-7 h-7 rounded-full bg-brand-purple-100 text-brand-purple-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                    {scene.scene_number ?? scene_index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{scene.title || `Scene ${scene_index + 1}`}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {scene.duration > 0 && (
                                        <span className="text-xs text-gray-500">{Math.round(scene.duration)}s</span>
                                      )}
                                      {scene.media_urls?.s3_video_url && (
                                        <span className="text-xs text-green-600 flex items-center gap-0.5">
                                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block"></span>
                                          video
                                        </span>
                                      )}
                                      {scene.narration && (
                                        <span className="text-xs text-gray-400 truncate max-w-[200px]">{scene.narration.substring(0, 60)}{scene.narration.length > 60 ? '...' : ''}</span>
                                      )}
                                    </div>
                                  </div>
                                  {canEditItems && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!locked && !scene.is_regenerating) {
                                          setEditingSceneRef({ sceneId: scene._id!, contentItemId: item._id! });
                                        }
                                      }}
                                      disabled={locked || scene.is_regenerating}
                                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                                        locked || scene.is_regenerating
                                          ? 'cursor-not-allowed opacity-50'
                                          : 'hover:bg-brand-purple-100'
                                      }`}
                                      title={scene.is_regenerating ? 'Scene is regenerating...' : locked ? lockedReason : 'Edit scene'}
                                    >
                                      <Edit className={`w-3.5 h-3.5 ${locked || scene.is_regenerating ? 'text-gray-400' : 'text-brand-purple-600'}`} />
                                    </button>
                                  )}
                                  {/* Delete scene */}
                                  {canEditItems && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!scene.is_regenerating) {
                                          setDeletingScene({ scene, contentItemId: item._id! });
                                        }
                                      }}
                                      disabled={scene.is_regenerating}
                                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                                        scene.is_regenerating ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-100'
                                      }`}
                                      title={scene.is_regenerating ? 'Scene is regenerating...' : 'Delete scene'}
                                    >
                                      <Trash2 className={`w-3.5 h-3.5 ${scene.is_regenerating ? 'text-gray-400' : 'text-red-600'}`} />
                                    </button>
                                  )}
                                  </>
                                  )}
                                </SortableScene>
                                );
                              })}
                            </div>
                            </SortableContext>
                            </DndContext>
                          </div>
                        )}
                      </div>
                    )}

                    {(item.type === 'html' || item.type === 'html_with_audio') && (
                      <div>
                        <div className={`rounded-lg ${getThemeClass()} ${getContentFormatClass()}`}>
                          <div className={`${getThemeClass()} ${getContentFormatClass()}`}>
                            <div className="prose prose-base max-w-none">
                              {item.content_source === 'uploaded' && item.media_assets?.s3_html_url ? (
                                // Uploaded PDF (embedded). Rendered in a plain,
                                // NON-sandboxed iframe — Chrome disables its PDF
                                // viewer inside a sandboxed frame ("blocked by Chrome").
                                <div className="h-[600px] min-h-[600px]">
                                  <iframe
                                    src={item.media_assets.s3_html_url}
                                    title={item.title}
                                    className="w-full h-full border-0 rounded-lg bg-white"
                                  />
                                </div>
                              ) : item.media_assets?.s3_html_url ? (
                                <div className="h-[600px] min-h-[600px]">
                                  <HTMLContentPlayer
                                    htmlUrl={item.media_assets.s3_html_url}
                                    audioUrl={item.type === 'html_with_audio' ? (item.media_assets.s3_audio_url || item.scenes?.[0]?.media_urls?.s3_audio_url) : undefined}
                                    title={item.title}
                                    duration={item.estimated_time?.minutes || 1}
                                    isPlaying={getAudioPlayerState(item._id || item.id || '').isPlaying}
                                    playbackRate={getAudioPlayerState(item._id || item.id || '').playbackRate}
                                    volume={getAudioPlayerState(item._id || item.id || '').volume}
                                    onPlayPause={() => handleAudioPlayPause(item._id || item.id || '')}
                                    onPlaybackRateChange={() => handleAudioPlaybackRateChange(item._id || item.id || '')}
                                    onVolumeChange={(volume) => handleAudioVolumeChange(item._id || item.id || '', volume)}
                                  />
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Monitor className="w-8 h-8 text-gray-400" />
                                  </div>
                                  <p className="text-gray-500 font-medium">HTML content not available</p>
                                  <p className="text-sm text-gray-400 mt-1">The HTML content is currently being prepared</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SCORM lesson preview */}
                    {item.type === 'scorm' && (
                      <ScormPreview item={item} />
                    )}

                    {/* Content-level Assessments - Display inside each content item */}
                    {(item.pre_assessment || item.post_assessment ||
                      (canManageAssessments && !!item._id && !!onAddAssessment)) && (
                      <div className="p-3 space-y-3 bg-gray-50 border-t border-gray-200">
                        {/* Add buttons for missing pre/post slots */}
                        {canManageAssessments && item._id && onAddAssessment &&
                          (!item.pre_assessment?.id || !item.post_assessment?.id) && (
                          <div className="flex flex-wrap items-center gap-2">
                            {!item.pre_assessment?.id && (
                              <button
                                onClick={() => onAddAssessment('PRE', item._id!)}
                                className="px-3 py-1.5 rounded-lg border-2 border-dashed border-brand-blue-300 text-brand-blue-700 text-xs font-medium hover:bg-brand-blue-50 transition-colors flex items-center gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add Pre-Assessment
                              </button>
                            )}
                            {!item.post_assessment?.id && (
                              <button
                                onClick={() => onAddAssessment('POST', item._id!)}
                                className="px-3 py-1.5 rounded-lg border-2 border-dashed border-brand-green-300 text-brand-green-700 text-xs font-medium hover:bg-brand-green-50 transition-colors flex items-center gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add Post-Assessment
                              </button>
                            )}
                          </div>
                        )}
                        {/* Pre-Assessment */}
                        {item.pre_assessment && item.pre_assessment.id && (
                          <div className="p-3 rounded-lg border border-brand-blue-300 bg-brand-blue-50">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ClipboardCheck className="w-4 h-4 text-brand-blue-600" />
                                  <span className="text-xs font-bold text-gray-900">Pre-Assessment</span>
                                  {item.pre_assessment.required && (
                                    <span className="px-2 py-0.5 bg-brand-blue-600 text-white rounded-full text-xs font-medium">Required</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => onViewAssessment?.(item.pre_assessment!.id)}
                                    className="px-3 py-1 bg-brand-blue-600 text-white rounded text-xs font-medium hover:bg-brand-blue-700 transition-colors"
                                  >
                                    View Details
                                  </button>
                                  {canManageAssessments && onDeleteAssessment && (
                                    <button
                                      onClick={() => onDeleteAssessment(item.pre_assessment!.id, 'Pre-Assessment')}
                                      className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                      title="Delete assessment"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-700">
                                {item.pre_assessment.title && (
                                  <span className="font-medium">{item.pre_assessment.title}</span>
                                )}
                                <span className="px-2 py-0.5 bg-white text-brand-blue-700 rounded border border-brand-blue-200">
                                  Pass: {item.pre_assessment.passing_score}%
                                </span>
                                <span className="px-2 py-0.5 bg-white text-brand-blue-700 rounded border border-brand-blue-200">
                                  {item.pre_assessment.total_questions ?? 0} Questions
                                </span>
                              </div>
                              {item.pre_assessment.description && (
                                <p className="text-xs text-gray-600 mt-1">{item.pre_assessment.description}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Post-Assessment */}
                        {item.post_assessment && item.post_assessment.id && (
                          <div className="p-3 rounded-lg border border-brand-green-300 bg-brand-green-50">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ClipboardCheck className="w-4 h-4 text-brand-green-600" />
                                  <span className="text-xs font-bold text-gray-900">Post-Assessment</span>
                                  {item.post_assessment.required && (
                                    <span className="px-2 py-0.5 bg-brand-green-600 text-white rounded-full text-xs font-medium">Required</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => onViewAssessment?.(item.post_assessment!.id)}
                                    className="px-3 py-1 bg-brand-green-600 text-white rounded text-xs font-medium hover:bg-brand-green-700 transition-colors"
                                  >
                                    View Details
                                  </button>
                                  {canManageAssessments && onDeleteAssessment && (
                                    <button
                                      onClick={() => onDeleteAssessment(item.post_assessment!.id, 'Post-Assessment')}
                                      className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                      title="Delete assessment"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-700">
                                {item.post_assessment.title && (
                                  <span className="font-medium">{item.post_assessment.title}</span>
                                )}
                                <span className="px-2 py-0.5 bg-white text-brand-green-700 rounded border border-brand-green-200">
                                  Pass: {item.post_assessment.passing_score}%
                                </span>
                                <span className="px-2 py-0.5 bg-white text-brand-green-700 rounded border border-brand-green-200">
                                  {item.post_assessment.total_questions ?? 0} Questions
                                </span>
                              </div>
                              {item.post_assessment.description && (
                                <p className="text-xs text-gray-600 mt-1">{item.post_assessment.description}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </SortableContentItem>
            );
          })}
            </SortableContext>
          </DndContext>

          {/* Add content item */}
          {canEditItems && moduleId && (
            <button
              onClick={() => setShowAddItem(true)}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg py-3 text-sm font-medium text-gray-500 hover:border-brand-blue-400 hover:text-brand-blue-600 hover:bg-brand-blue-50/40 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Content Item
            </button>
          )}
        </div>
      )}

      {/* Add Content Item Modal */}
      <AddContentItemModal
        isOpen={showAddItem}
        onSave={handleAddContentItem}
        onClose={() => setShowAddItem(false)}
      />

      {/* Edit Content Item Modal */}
      {editingContentItem && (
        <EditContentItemModal
          contentItem={editingContentItem}
          courseLanguage={courseLanguage}
          courseCompleted={!isGenerating}
          moduleId={moduleId || items[0]?._id || ''}
          onClose={() => setEditingContentItem(null)}
          onSuccess={async () => {
            setEditingContentItem(null);
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
        />
      )}

      {/* Upload Document Modal (empty html / html_with_audio items) */}
      {uploadDocItem && (
        <UploadContentFileModal
          isOpen={!!uploadDocItem}
          contentItemId={uploadDocItem._id!}
          contentItemTitle={uploadDocItem.title}
          hasExistingContent={!isHtmlEffectivelyEmpty(uploadDocItem.content)}
          onClose={() => setUploadDocItem(null)}
          onSuccess={async () => {
            setUploadDocItem(null);
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
        />
      )}

      {/* Upload Video Modal (scene-less video_presentation items) */}
      {uploadVideoItem && (
        <UploadContentVideoModal
          isOpen={!!uploadVideoItem}
          contentItemId={uploadVideoItem._id!}
          contentItemTitle={uploadVideoItem.title}
          onClose={() => setUploadVideoItem(null)}
          onSuccess={async () => {
            setUploadVideoItem(null);
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
        />
      )}

      {/* Edit uploaded-video content item (title + estimated time only) */}
      {editingVideoItem && (
        <EditVideoContentItemModal
          contentItem={editingVideoItem}
          onClose={() => setEditingVideoItem(null)}
          onSuccess={async () => {
            setEditingVideoItem(null);
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
        />
      )}

      {/* Edit Scene Modal */}
      {editingScene && (
        <EditSceneModal
          scene={editingScene.scene}
          contentItemId={editingScene.contentItemId}
          courseLanguage={courseLanguage}
          theme={theme}
          videoRenderingMode={videoRenderingMode}
          onClose={() => setEditingSceneRef(null)}
          onSuccess={async () => {
            setEditingSceneRef(null);
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
          onSceneUpdated={async () => {
            // Silent refresh after AI HTML regenerate — updates the list/preview
            // without unmounting the modal (editingSceneRef derives fresh scene).
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
        />
      )}

      {/* Edit Avatar Video Modal — stays open after revision/regen so the user
          can review the updated video and continue iterating without reopening. */}
      {editingAvatarVideo && (
        <EditAvatarVideoModal
          contentItem={editingAvatarVideo}
          courseId={editingAvatarVideo.course_id || ''}
          onClose={() => setEditingAvatarVideo(null)}
          onVideoUpdated={async () => {
            // Refresh the parent list silently so the underlying card has the
            // new URL when the user eventually closes the modal — but DO NOT
            // unmount the modal here.
            if (onRefresh && moduleId) {
              await onRefresh(moduleId);
            }
          }}
        />
      )}

      {/* Delete Content Item Confirmation */}
      <ConfirmationModal
        isOpen={!!deletingItem}
        type="danger"
        title="Delete Content Item"
        message={`Are you sure you want to delete "${deletingItem?.title || 'this content item'}"? This removes the content item and its scenes. This action cannot be undone.`}
        confirmText={isDeletingItem ? 'Deleting...' : 'Delete Content Item'}
        cancelText="Cancel"
        onConfirm={handleDeleteItem}
        onCancel={() => setDeletingItem(null)}
      />

      {/* Add Scene Modal */}
      <AddSceneModal
        isOpen={!!addSceneForItemId}
        onSave={(data) => handleAddScene(addSceneForItemId!, data.title)}
        onClose={() => setAddSceneForItemId(null)}
      />

      {/* Delete Scene Confirmation */}
      <ConfirmationModal
        isOpen={!!deletingScene}
        type="danger"
        title="Delete Scene"
        message={`Are you sure you want to delete "${deletingScene?.scene?.title || 'this scene'}"? This action cannot be undone.`}
        confirmText={isDeletingScene ? 'Deleting...' : 'Delete Scene'}
        cancelText="Cancel"
        onConfirm={handleDeleteScene}
        onCancel={() => setDeletingScene(null)}
      />
    </div>
  );
}
